import { MaterialIcons, SimpleLineIcons } from "@expo/vector-icons";
import AsyncStorage from "@react-native-async-storage/async-storage";
import * as Clipboard from "expo-clipboard";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import {
  AccessibilityInfo,
  ActivityIndicator,
  Animated,
  BackHandler,
  I18nManager,
  Platform,
  SafeAreaView,
  ScrollView,
  StatusBarStyle,
  StyleSheet,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import {
  Badge,
  CreateThemeOptions,
  Header,
  Button as RNEButton,
  Text,
  ThemeContext,
  ThemeProvider,
  createTheme,
  useTheme,
} from "react-native-elements";
import "react-native-gesture-handler";
import {
  Gesture,
  GestureDetector,
  GestureHandlerRootView,
  SimultaneousGesture,
} from "react-native-gesture-handler";
import {
  SafeAreaProvider,
  initialWindowMetrics,
} from "react-native-safe-area-context";
import { clearAllCaches } from "./backend/Caching";
import Demo from "./backend/Demo";
import { clearFlag, hasFlag, setFlag } from "./backend/Flags";
import {
  PLAYER_INITIAL_STATE,
  PlayerContext,
  PlayerContextState,
  PlayerContextTransform,
} from "./backend/PlayerContext";
import {
  addContextListener,
  applyAllTransforms,
  clearContextListener,
} from "./backend/PlayerContextListeners";
import { loadData, saveData, serializeSaveData } from "./backend/SaveData";
import { getUICommands } from "./backend/UICommands";
import { getEventMessageById } from "./backend/events/Events";
import { SpellElement } from "./backend/spells/ElementsAndSchools";
import { nextFrame } from "./backend/timetick/TimeTick";
import { getSecondsPlayed } from "./backend/timetick/TotalTimePlayed";
import About from "./components/About";
import { Autocast } from "./components/Autocast";
import Bestiary from "./components/Bestiary";
import Campus from "./components/Campus";
import Creation from "./components/Creation";
import { Creatures } from "./components/Creatures";
import Debug from "./components/Debug";
import Ending from "./components/Ending";
import Exploration from "./components/Exploration";
import Help from "./components/Help";
import Intro from "./components/Intro";
import Inventory from "./components/Inventory";
import { Loading } from "./components/Loading";
import Options from "./components/Options";
import { Research } from "./components/Research";
import Retirement from "./components/Retirement";
import Stats from "./components/Stats";
import Strengthening from "./components/Strengthening";
import Upgrades from "./components/Upgrades";
import { EventList } from "./components/events/EventList";
import { EventMenu } from "./components/events/EventMenu";
import { EventOverlay } from "./components/events/EventOverlay";
import { BottomMenu } from "./components/main/BottomMenu";
import GameTickProcessor from "./components/main/GameTickProcessor";
import ManaBar from "./components/main/ManaBar";
import { Menu, MenuContents } from "./components/main/Menu";
import { SpellMenu, SpellMenuContents } from "./components/spells/SpellMenu";
import { AccessibilityContext } from "./components/utility/AccessibilityContext";
import { BottomResourceViewer } from "./components/utility/BottomResourceViewer";
import { Button } from "./components/utility/Button";
import { ErrorBoundary } from "./components/utility/ErrorBoundary";
import { FullScreenListener } from "./components/utility/FullScreenListener";
import { KeepAwake } from "./components/utility/KeepAwake";
import {
  elementColorByTheme,
  getAllSections,
  getLayoutType,
  useGestureHandler,
} from "./components/utility/Miscellaneous";
import { PlayerContextFreezer } from "./components/utility/PlayerContextFreezer";
import { SaveDataBackupper } from "./components/utility/SaveDataBackupper";
import { Text as UtilityText } from "./components/utility/Text";
import { TooltipDisplay } from "./components/utility/Tooltip";
import {
  TooltipContent,
  TooltipContext,
  TooltipControlContext,
  TooltipPosition,
} from "./components/utility/TooltipContext";
import {
  UIContext,
  useUIStateFromContext,
} from "./components/utility/UIContext";
import { VisibilityContext } from "./components/utility/VisibilityContext";
import { clone } from "./utils/CoreUtils";
import { SAVE_TO_FILE_SUPPORTED, saveToFile } from "./utils/FilesystemSave";
import { formatNumber } from "./utils/FormattingUtils";
import { OrientationLockManager } from "./utils/OrientationLockManager";
import {
  getCurrentVersion,
  getCurrentVersionTimestamp,
} from "./utils/Versioning";
import { ZoomChecker } from "./utils/Zoom";

I18nManager.allowRTL(false);

const dividerColorDark =
  StyleSheet.hairlineWidth < 1 ? "#666" : "rgba(255, 255, 255, 0.12)";

const lightCommonColors = {
  hpBarForeground: "#f00",
  hpBarBackground: "#800",
  progressBar: "#07f",
  background: "#fff",
  disabled: "#eee",
  grey0: "#333",
  grey1: "#444",
  grey2: "#666",
  grey3: "#888",
  grey4: "#ccc",
  grey5: "#eee",
  cappedResource: "#00c",
  warning: "#e90",
};

const darkCommonColors = {
  divider: dividerColorDark,
  disabled: "#363636",
  grey5: "#303030",
  grey4: "#444",
  grey3: "#666",
  grey2: "#888",
  grey1: "#ccc",
  grey0: "#eee",
  background: "#222",
  hpBarForeground: "#a00",
  hpBarBackground: "#500",
  progressBar: "#06c",
  cappedResource: "#bbf",
  error: "#f00",
};

const primaryColorsByElement: Record<string, any> = {
  Default: {
    lightColors: { ...lightCommonColors },
    darkColors: { primary: "#247", ...darkCommonColors },
  },
  Fire: {
    lightColors: {
      primary: elementColorByTheme("light", SpellElement.Fire),
      ...lightCommonColors,
    },
    darkColors: {
      primary: elementColorByTheme("dark", SpellElement.Fire),
      ...darkCommonColors,
    },
  },
  Earth: {
    lightColors: {
      primary: elementColorByTheme("light", SpellElement.Earth),
      ...lightCommonColors,
    },
    darkColors: {
      primary: elementColorByTheme("dark", SpellElement.Earth),
      ...darkCommonColors,
    },
  },
  Water: {
    lightColors: {
      primary: elementColorByTheme("light", SpellElement.Water),
      ...lightCommonColors,
    },
    darkColors: {
      primary: elementColorByTheme("dark", SpellElement.Water),
      ...darkCommonColors,
    },
  },
  Air: {
    lightColors: {
      primary: elementColorByTheme("light", SpellElement.Air),
      ...lightCommonColors,
    },
    darkColors: {
      primary: elementColorByTheme("dark", SpellElement.Air),
      ...darkCommonColors,
    },
  },
  Nature: {
    lightColors: {
      primary: elementColorByTheme("light", SpellElement.Nature),
      ...lightCommonColors,
    },
    darkColors: {
      primary: elementColorByTheme("dark", SpellElement.Nature),
      ...darkCommonColors,
    },
  },
  Lightning: {
    lightColors: {
      primary: elementColorByTheme("light", SpellElement.Lightning),
      ...lightCommonColors,
    },
    darkColors: {
      primary: elementColorByTheme("dark", SpellElement.Lightning),
      ...darkCommonColors,
    },
  },
  Darkness: {
    lightColors: {
      primary: elementColorByTheme("light", SpellElement.Darkness),
      ...lightCommonColors,
    },
    darkColors: {
      primary: elementColorByTheme("dark", SpellElement.Darkness),
      ...darkCommonColors,
    },
  },
};

function overrideShouldComponentUpdate(
  this: any,
  nextProps: any,
  nextState: any,
) {
  return (
    this.props.theme != nextProps.theme ||
    JSON.stringify(this.state) != JSON.stringify(nextState)
  );
}

const memoizedThemes: Record<string, any> = {};
const memoizedTheme = (key: any, theme: any) => {
  const stringified = JSON.stringify(key);
  if (!memoizedThemes[stringified]) {
    memoizedThemes[stringified] = theme;
  }
  return memoizedThemes[stringified];
};

function overrideRender(this: any) {
  const value = memoizedTheme(this.state.theme, {
    theme: this.state.theme,
    updateTheme: this.updateTheme,
    replaceTheme: this.replaceTheme,
  });
  return (
    <ThemeContext.Provider value={value}>
      {this.props.children}
    </ThemeContext.Provider>
  );
}
ThemeProvider.prototype.shouldComponentUpdate = overrideShouldComponentUpdate;
ThemeProvider.prototype.render = overrideRender;

function ThemeUpdater() {
  const theme = useTheme();
  const playerContext = useContext(PlayerContext);

  const fullTheme = useMemo((): CreateThemeOptions => {
    return {
      lightColors:
        primaryColorsByElement?.[
          (playerContext.global.overrideElementDisplay != "no"
            ? playerContext.global.overrideElementDisplay
            : playerContext.primaryElement) || "Default"
        ]?.lightColors,
      darkColors:
        primaryColorsByElement?.[
          (playerContext.global.overrideElementDisplay != "no"
            ? playerContext.global.overrideElementDisplay
            : playerContext.primaryElement) || "Default"
        ]?.darkColors,
      mode: playerContext.global.themeMode,
    };
  }, [
    primaryColorsByElement,
    playerContext.global.overrideElementDisplay,
    playerContext.primaryElement,
    playerContext.global.themeMode,
  ]);

  useEffect(() => {
    theme.replaceTheme(fullTheme);
  }, [fullTheme]);

  return null;
}

function IntroContents() {
  const theme = useTheme();
  const statusBarProps = useMemo(
    () => ({
      translucent: true,
      backgroundColor: "transparent",
      barStyle: "light-content" as StatusBarStyle,
    }),
    [],
  );
  return (
    <View style={styles.maxContainer} nativeID={theme.theme.mode}>
      <Header
        containerStyle={[
          styles.headerContainer,
          { borderBottomColor: theme.theme.colors.background },
        ]}
        statusBarProps={statusBarProps}
        centerComponent={{
          text: "Welcome to Magic Research",
          style: { color: "#fff" },
          accessibilityRole: "header",
        }}
      />
      <OrientationLockManager />
      <SafeAreaView style={styles.safeAreaContainer}>
        <View
          style={[
            styles.biggerContainer,
            { backgroundColor: theme.theme.colors.background },
          ]}
        >
          <View style={styles.introContainer}>
            <ScrollView contentContainerStyle={styles.container}>
              <Intro />
            </ScrollView>
          </View>
        </View>
      </SafeAreaView>
    </View>
  );
}

type MainAppContentsProps = {
  setPlayerState: any;
  onLoadingProgress: any;
};

function resetKeys(pinnedSectionObjects: { section: string; key: number }[]) {
  for (let idx in pinnedSectionObjects) {
    pinnedSectionObjects[idx].key = Number.parseInt(idx) + 1;
  }
  return pinnedSectionObjects;
}

function MainAppContents(props: MainAppContentsProps) {
  const { height, width } = useWindowDimensions();
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;
  const uiContext = useContext(UIContext);

  const [menuShown, setMenuShown] = useState(false);
  const [rightMenuShown, setRightMenuShown] = useState("none");
  const layout = getLayoutType(height, width);

  const allSections = getAllSections();
  const sectionData = Object.fromEntries(
    allSections.map((section) => [section.name, section]),
  );

  const [section, setSection] = useState("Campus");
  const [pinnedSectionObjects, setPinnedSectionObjects] = useState<
    { section: string; key: number }[]
  >([]);
  const pinnedSectionObjectsRef = useRef(pinnedSectionObjects);
  pinnedSectionObjectsRef.current = pinnedSectionObjects;

  const activeSections = [
    section,
    ...pinnedSectionObjects.map((pinnedSection) => pinnedSection.section),
  ];
  const [_uiActiveSections, setUiActiveSections] = useUIStateFromContext(
    uiContext,
    "active_sections",
    activeSections,
  );

  useEffect(() => {
    setUiActiveSections(activeSections);
  }, [JSON.stringify(activeSections)]);

  useEffect(() => {
    if (layout.activeContentTabs == 1) {
      setPinnedSectionObjects([]);
    }
    const newSectionObjects = pinnedSectionObjectsRef.current.slice(
      0,
      layout.activeContentTabs - 1,
    );
    setPinnedSectionObjects(resetKeys(newSectionObjects));
  }, [layout.activeContentTabs]);

  const togglePinnedSection = useCallback(
    (section: string) => {
      // If the section is already there, remove it
      if (pinnedSectionObjectsRef.current.find((s) => s.section == section)) {
        setPinnedSectionObjects(
          pinnedSectionObjectsRef.current.filter((s) => s.section != section),
        );
        return;
      }

      // Otherwise, choose the smallest possible index, and push it to the end
      // Obtain the lowest free key, so that we are constantly reusing numbers
      // so that we don't need to re-load things
      const usedIndices = pinnedSectionObjectsRef.current.map((s) => s.key);
      let lowestFreeIndex = 1;
      while (usedIndices.includes(lowestFreeIndex)) {
        lowestFreeIndex++;
      }
      const newPinnedSectionObjects = [
        ...pinnedSectionObjectsRef.current,
        { section: section, key: lowestFreeIndex },
      ];
      setPinnedSectionObjects(
        newPinnedSectionObjects.slice(0, layout.activeContentTabs - 1),
      );

      // Finally, if the current selected section is the one that was pinned,
      // we want to reset it to something new
    },
    [setPinnedSectionObjects, JSON.stringify(layout)],
  );

  const theme = useTheme();

  const processUICommand = useCallback(
    (uiCommand: string) => {
      if (
        uiCommand == "goToExploration" &&
        !activeSections.includes("Exploration")
      ) {
        setSection("Exploration");
      } else if (
        uiCommand == "goToEnding" &&
        !activeSections.includes("Ending")
      ) {
        setSection("Ending");
        setPinnedSectionObjects([]);
      } else if (uiCommand == "closeAllMenus") {
        setMenuShown(false);
        setRightMenuShown("none");
      }
    },
    [setSection, activeSections, setPinnedSectionObjects],
  );

  const processUICommands = useCallback(
    (state: PlayerContextState) => {
      const uiCommands = getUICommands(state);
      uiCommands.forEach(processUICommand);
      state.uiCommands = uiCommands.filter(
        (uiCommand) =>
          uiCommand != "goToExploration" &&
          uiCommand != "goToEnding" &&
          uiCommand != "closeAllMenus",
      );
      return state;
    },
    [processUICommand],
  );

  useEffect(() => {
    addContextListener("appUiCommand", (newState) => {
      const uiCommands = getUICommands(newState);
      if (
        uiCommands.filter((command) =>
          ["goToExploration", "goToEnding", "closeAllMenus"].includes(command),
        ).length > 0
      ) {
        return (state) => processUICommands(state);
      }
    });
    return () => clearContextListener("appUiCommand");
  }, [processUICommands]);

  const onMenuPress = useCallback(() => {
    setMenuShown(!menuShown);
    setRightMenuShown("none");
  }, [menuShown]);

  const onEventMenuPress = useCallback(() => {
    if (rightMenuShown !== "event") {
      setRightMenuShown("event");
    } else {
      setRightMenuShown("none");
    }
    setMenuShown(false);
  }, [rightMenuShown]);

  const onSpellMenuPress = useCallback(() => {
    if (rightMenuShown !== "spell") {
      setRightMenuShown("spell");
    } else {
      setRightMenuShown("none");
    }
    setMenuShown(false);
  }, [rightMenuShown]);

  const onNavigationPress = useCallback(
    (section: string) => {
      setSection(section);
      setMenuShown(false);
      setRightMenuShown("none");
    },
    [setSection],
  );

  const ongoingEventCount = Object.values(
    playerContext.eventOccurrences,
  ).filter((event) => {
    return (
      event.currentMessageId &&
      !getEventMessageById(event.currentMessageId).isTerminal() &&
      getEventMessageById(event.currentMessageId).isActionable(
        playerContext,
        event.params,
      )
    );
  }).length;

  useEffect(() => {
    setSection("Campus");
    setPinnedSectionObjects([]);
  }, [playerContext.global.totalTimesResetted]);

  const showBottomResourceBar =
    !layout.wideLayout &&
    !sectionData[activeSections[0]]?.hideBottomResourceBar;

  const centerComponent = useMemo(
    () => (
      <View style={styles.centerText}>
        <Text accessibilityRole="header" style={{ color: "#fff" }}>
          {activeSections.length == 1 ? activeSections[0] : "Magic Research"}
        </Text>
      </View>
    ),
    [activeSections.join(), theme],
  );

  const bottomOpacityAnim = useRef(
    new Animated.Value(showBottomResourceBar ? 0.5 : 0),
  ).current;

  useEffect(() => {
    if (showBottomResourceBar) {
      Animated.timing(bottomOpacityAnim, {
        toValue: 1.0,
        duration: 150,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(bottomOpacityAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }).start();
    }
  }, [showBottomResourceBar]);

  const enableSwiping = playerContext.global.enableSwiping;

  const swipeLeftHandler = useCallback(() => {
    if (!enableSwiping) {
      return;
    }
    if (menuShown) {
      setMenuShown(false);
    } else if (rightMenuShown === "none") {
      setRightMenuShown("spell");
    }
  }, [
    menuShown,
    setMenuShown,
    rightMenuShown,
    setRightMenuShown,
    enableSwiping,
  ]);
  const swipeRightHandler = useCallback(() => {
    if (!enableSwiping) {
      return;
    }
    if (rightMenuShown != "none") {
      setRightMenuShown("none");
    } else if (!menuShown && !layout.showMenu) {
      setMenuShown(true);
    }
  }, [
    menuShown,
    setMenuShown,
    rightMenuShown,
    setRightMenuShown,
    enableSwiping,
  ]);

  const compositeGestures: SimultaneousGesture[] = [];
  const compositeGesturesExploration: SimultaneousGesture[] = [];
  for (let i = 0; i < 1; i++) {
    const swipeHandlerGesture = useGestureHandler(
      swipeLeftHandler,
      swipeRightHandler,
    );
    const nativeGesture = useMemo(() => Gesture.Native(), []);
    const compositeGesture = useMemo(
      () => Gesture.Simultaneous(swipeHandlerGesture, nativeGesture),
      [swipeHandlerGesture, nativeGesture],
    );
    const swipeHandlerGestureExploration = useGestureHandler(
      swipeLeftHandler,
      swipeRightHandler,
    );
    const nativeGestureExploration = useMemo(() => Gesture.Native(), []);
    const compositeGestureExploration = useMemo(
      () =>
        Gesture.Simultaneous(
          swipeHandlerGestureExploration,
          nativeGestureExploration,
        ),
      [swipeHandlerGestureExploration, nativeGestureExploration],
    );
    compositeGestures.push(compositeGesture);
    compositeGesturesExploration.push(compositeGestureExploration);
  }

  const scrollRefs = Object.fromEntries(
    allSections.map((section) => [section.name, useRef<ScrollView>()]),
  );

  const [scrollPositions, setScrollPositions] = useUIStateFromContext<
    Record<string, number>
  >(uiContext, "mainScrollPositions", {});
  const onScrollPerSection = useMemo(() => {
    return Object.fromEntries(
      allSections.map((sectionData) => [
        sectionData.name,
        (event: any) => {
          const scrollValue = event.nativeEvent.contentOffset.y;
          scrollPositions[sectionData.name] = scrollValue;
          setScrollPositions(scrollPositions);
        },
      ]),
    );
  }, []);
  useEffect(() => {
    for (let section of activeSections) {
      if (!sectionData[section]?.scrollable) {
        return;
      }
      const shouldScroll = Platform.select({ default: true, web: false });
      if (!shouldScroll) {
        return;
      }
      scrollRefs[section]?.current?.scrollTo({
        y: scrollPositions?.[section] ?? 0,
        animated: false,
      });
    }
  }, [activeSections.join(",")]);

  const showBottomMenu = playerContext.global.enableBottomMenu;
  const leftHandedMode = playerContext.global.leftHandedMode;
  const putCastSpellOnBottomMenu =
    playerContext.global.putCastSpellOnBottomMenu;

  function wrapInSection(
    component: React.ReactElement,
    sectionTitle: string,
    activeSection: string,
  ) {
    return (
      <View style={activeSection != sectionTitle && { display: "none" }}>
        <PlayerContextFreezer isVisible={activeSection == sectionTitle}>
          {component}
        </PlayerContextFreezer>
      </View>
    );
  }

  const sectionIndices = useMemo(() => {
    const indices = [];
    for (let i = 0; i < layout.activeContentTabs; i++) {
      indices.push(i);
    }
    return indices;
  }, [layout.activeContentTabs]);

  const mainSections = useMemo(() => {
    let unusedIndex = 1;
    return (
      <View
        accessibilityElementsHidden={menuShown || rightMenuShown != "none"}
        importantForAccessibility={
          menuShown || rightMenuShown != "none" ? "no-hide-descendants" : "auto"
        }
        style={{
          flex: 1,
          flexDirection: "row",
          alignItems: "stretch",
          justifyContent: "space-evenly",
        }}
      >
        {sectionIndices.map((index) => {
          if (layout.activeContentTabs == 1 && index > 0) {
            // Optimization for phones and similar
            return null;
          }
          const currentSection =
            index == 0
              ? section
              : pinnedSectionObjects[index - 1]?.section ?? "-";
          let sectionKey =
            index == 0 ? 0 : pinnedSectionObjects?.[index - 1]?.key;
          if (sectionKey === undefined) {
            while (pinnedSectionObjects.find((s) => s.key == unusedIndex)) {
              unusedIndex++;
            }
            sectionKey = unusedIndex;
            unusedIndex++;
          }
          const showHeader = pinnedSectionObjects.length > 0;

          const scrollViewContents = (
            <ScrollView
              //@ts-ignore Not sure why this is giving error
              ref={scrollRefs[currentSection]}
              style={styles.mainContainer}
              onScroll={onScrollPerSection[currentSection]}
              contentContainerStyle={
                currentSection != "Inventory" && styles.container
              }
            >
              {wrapInSection(<Research />, "Research", currentSection)}
              {wrapInSection(<Inventory />, "Inventory", currentSection)}
              {wrapInSection(<Upgrades />, "Upgrades", currentSection)}
              {wrapInSection(<Stats />, "Stats", currentSection)}
              {wrapInSection(<Bestiary />, "Bestiary", currentSection)}
              {wrapInSection(<Creatures />, "Creatures", currentSection)}
              {wrapInSection(<Strengthening />, "Strengthen", currentSection)}
              {wrapInSection(<Retirement />, "Retirement", currentSection)}
              {wrapInSection(<Ending />, "Ending", currentSection)}
              {wrapInSection(<Creation />, "Creation", currentSection)}
              {wrapInSection(<Options />, "Options", currentSection)}
              {wrapInSection(<Help />, "Help", currentSection)}
              {wrapInSection(<About />, "About", currentSection)}
              {currentSection === "Debug" && <Debug />}
            </ScrollView>
          );
          const explorationContents = (
            <View style={styles.mainContainer}>
              <PlayerContextFreezer isVisible={currentSection == "Exploration"}>
                <Exploration />
              </PlayerContextFreezer>
            </View>
          );
          return (
            <VisibilityContext.Provider
              key={sectionKey ?? index}
              value={index < layout.activeContentTabs}
            >
              <View
                key={sectionKey ?? index}
                style={[
                  showHeader && {
                    marginHorizontal: 4,
                    borderWidth: 1,
                    borderColor: theme.theme.colors.divider,
                  },
                  {
                    flex: 1,
                    display: currentSection == "-" ? "none" : undefined,
                  },
                ]}
              >
                {showHeader && (
                  <View
                    style={[
                      {
                        height: 56,
                        paddingVertical: 8,
                        paddingHorizontal: 16,
                        flexDirection: "row",
                        alignItems: "center",
                        borderBottomWidth: 1,
                        borderBottomColor: theme.theme.colors.divider,
                      },
                    ]}
                  >
                    <View style={{ flex: 1 }}>
                      <UtilityText h2>{currentSection}</UtilityText>
                    </View>
                    {index > 0 && (
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <MaterialIcons
                          name="close"
                          size={20}
                          color={theme.theme.colors.black}
                          onPress={() => togglePinnedSection(currentSection)}
                          style={{ marginLeft: 8 }}
                          accessibilityRole="button"
                          accessibilityLabel={"Close Section"}
                        />
                      </View>
                    )}
                  </View>
                )}
                <View
                  style={[
                    { flex: 1 },
                    !sectionData[currentSection]?.scrollView && {
                      display: "none",
                    },
                  ]}
                >
                  {index > 0 ? (
                    scrollViewContents
                  ) : (
                    <GestureDetector
                      key="mainGesture"
                      gesture={compositeGestures?.[sectionKey]}
                    >
                      {scrollViewContents}
                    </GestureDetector>
                  )}
                </View>
                <View
                  style={[
                    styles.mainContainer,
                    currentSection !== "Campus" && { display: "none" },
                  ]}
                >
                  <PlayerContextFreezer isVisible={currentSection == "Campus"}>
                    <Campus
                      onSwipeLeft={swipeLeftHandler}
                      onSwipeRight={swipeRightHandler}
                    />
                  </PlayerContextFreezer>
                </View>
                <View
                  style={[
                    styles.mainContainer,
                    currentSection !== "Apprentices" && { display: "none" },
                  ]}
                >
                  <PlayerContextFreezer
                    isVisible={currentSection == "Apprentices"}
                  >
                    <Autocast
                      onSwipeLeft={swipeLeftHandler}
                      onSwipeRight={swipeRightHandler}
                    />
                  </PlayerContextFreezer>
                </View>
                <View
                  style={[
                    styles.mainContainer,
                    currentSection !== "Exploration" && { display: "none" },
                  ]}
                >
                  {index > 0 ? (
                    explorationContents
                  ) : (
                    <GestureDetector
                      key="explorationGesture"
                      gesture={compositeGesturesExploration[sectionKey]}
                    >
                      {explorationContents}
                    </GestureDetector>
                  )}
                </View>
              </View>
            </VisibilityContext.Provider>
          );
        })}
      </View>
    );
  }, [
    theme,
    menuShown,
    rightMenuShown,
    pinnedSectionObjects,
    sectionIndices,
    section,
    togglePinnedSection,
  ]);

  const backCallback = useCallback(() => {
    if (menuShown) {
      setMenuShown(false);
      return true;
    }
    if (rightMenuShown != "none") {
      setRightMenuShown("none");
      return true;
    }
    return false;
  }, [menuShown, rightMenuShown, setMenuShown, setRightMenuShown]);

  useEffect(() => {
    const handler = BackHandler.addEventListener(
      "hardwareBackPress",
      backCallback,
    );

    return () => handler.remove();
  }, [backCallback]);

  const contentsWide = useMemo(
    () => (
      <View
        style={[
          styles.biggerContainer,
          { backgroundColor: theme.theme.colors.background },
        ]}
        onAccessibilityEscape={
          menuShown || rightMenuShown != "none" ? backCallback : undefined
        }
      >
        <View style={styles.horizontalContainer}>
          <View
            style={styles.webMenuContainer}
            accessibilityElementsHidden={menuShown || rightMenuShown != "none"}
            importantForAccessibility={
              menuShown || rightMenuShown != "none"
                ? "no-hide-descendants"
                : "auto"
            }
          >
            <MenuContents
              visible={true}
              pinnedSections={pinnedSectionObjects.map((s) => s.section)}
              onPressFavorite={togglePinnedSection}
              onNavigationPress={onNavigationPress}
              onSwipeLeft={swipeLeftHandler}
              onSwipeRight={swipeRightHandler}
            />
          </View>
          {mainSections}
          {layout.showSpellMenu ? (
            <View
              style={styles.webRightMenuContainer}
              accessibilityElementsHidden={
                menuShown || rightMenuShown != "none"
              }
              importantForAccessibility={
                menuShown || rightMenuShown != "none"
                  ? "no-hide-descendants"
                  : "auto"
              }
            >
              <SpellMenuContents
                visible={true}
                onSwipeLeft={swipeLeftHandler}
                onSwipeRight={swipeRightHandler}
                favoritesOnBottom={false}
              />
            </View>
          ) : (
            <SpellMenu
              visible={rightMenuShown === "spell"}
              onBackgroundPress={onSpellMenuPress}
              onSwipeLeft={swipeLeftHandler}
              onSwipeRight={swipeRightHandler}
              favoritesOnBottom={false}
            />
          )}
          {layout.showEventsMenu ? (
            <View style={styles.webRightMenuContainer}>
              <ScrollView>
                <EventList
                  onSwipeLeft={swipeLeftHandler}
                  onSwipeRight={swipeRightHandler}
                />
              </ScrollView>
            </View>
          ) : (
            <EventMenu
              visible={rightMenuShown === "event"}
              onBackgroundPress={onEventMenuPress}
              onSwipeLeft={swipeLeftHandler}
              onSwipeRight={swipeRightHandler}
            />
          )}
        </View>

        <ManaBar />
      </View>
    ),
    [
      theme,
      JSON.stringify(layout),
      height,
      rightMenuShown,
      mainSections,
      JSON.stringify(pinnedSectionObjects),
      backCallback,
    ],
  );

  const contents = useMemo(
    () => (
      <View
        style={[
          styles.biggerContainer,
          { backgroundColor: theme.theme.colors.background },
        ]}
        onAccessibilityEscape={
          menuShown || rightMenuShown != "none" ? backCallback : undefined
        }
      >
        <View
          style={[
            Platform.OS === "web" ? { height: height - 72 } : { flex: 1 },
            styles.mainContainerPhone,
          ]}
        >
          <View style={{ flex: 1 }}>
            {mainSections}
            <PlayerContextFreezer isVisible={rightMenuShown === "spell"}>
              <SpellMenu
                visible={rightMenuShown === "spell"}
                onBackgroundPress={onSpellMenuPress}
                onSwipeLeft={swipeLeftHandler}
                onSwipeRight={swipeRightHandler}
                favoritesOnBottom={showBottomMenu && putCastSpellOnBottomMenu}
              />
            </PlayerContextFreezer>
            <PlayerContextFreezer isVisible={rightMenuShown === "event"}>
              <EventMenu
                visible={rightMenuShown === "event"}
                onBackgroundPress={onEventMenuPress}
                onSwipeLeft={swipeLeftHandler}
                onSwipeRight={swipeRightHandler}
              />
            </PlayerContextFreezer>
          </View>
          {showBottomResourceBar && (
            <Animated.View
              style={[
                {
                  opacity: bottomOpacityAnim,
                  borderTopWidth: 1.0,
                  borderTopColor: theme.theme.colors.divider,
                },
              ]}
            >
              <View
                style={styles.accessibilityHidden}
                accessible={true}
                accessibilityLabel="Resources"
                accessibilityRole="header"
                accessibilityElementsHidden={false}
              />
              <BottomResourceViewer />
            </Animated.View>
          )}
          {showBottomMenu && (
            <>
              {leftHandedMode && putCastSpellOnBottomMenu && (
                <View
                  style={{
                    left: -46,
                    bottom: -110,
                    position: "absolute",
                    zIndex: 120,
                  }}
                >
                  <Button
                    accessibilityLabel={"Cast Spell"}
                    hitSlop={{ top: 50, left: 20, bottom: 20, right: 50 }}
                    onPressIn={onSpellMenuPress}
                    onPress={() => {}}
                    enableAutoRepress={false}
                    containerStyle={{
                      borderTopRightRadius: 100,
                      width: 144,
                      height: 168,
                    }}
                    buttonStyle={{
                      paddingRight: 92,
                      paddingTop: 92,
                      paddingLeft: 4,
                      borderTopRightRadius: 100,
                      borderTopWidth: 2,
                      borderRightWidth: 2,
                      borderColor: theme.theme.colors.divider,
                      width: 144,
                      height: 76,
                    }}
                    icon={
                      <SimpleLineIcons
                        style={{
                          position: "relative",
                          left: 50,
                          bottom: 62,
                          width: 24,
                          height: 24,
                          overflow: "visible",
                        }}
                        name="magic-wand"
                        size={24}
                        color="#fff"
                      />
                    }
                  ></Button>
                </View>
              )}
              <View
                style={{
                  borderTopWidth: 1.0,
                  borderTopColor: theme.theme.colors.divider,
                }}
              >
                <BottomMenu
                  onNavigationPress={onNavigationPress}
                  onSpellMenuPress={onSpellMenuPress}
                  selected={activeSections[0]}
                  leftHanded={leftHandedMode}
                  showCastSpellButton={putCastSpellOnBottomMenu}
                />
              </View>
              {!leftHandedMode && putCastSpellOnBottomMenu && (
                <View
                  style={{
                    right: -144,
                    bottom: -110,
                    position: "absolute",
                    zIndex: 120,
                  }}
                >
                  <Button
                    accessibilityLabel={"Cast Spell"}
                    hitSlop={{ top: 50, left: 50, bottom: 20, right: 20 }}
                    onPressIn={onSpellMenuPress}
                    onPress={() => {}}
                    enableAutoRepress={false}
                    containerStyle={{
                      borderTopLeftRadius: 100,
                      width: 240,
                      height: 168,
                    }}
                    buttonStyle={{
                      paddingLeft: 92,
                      paddingTop: 92,
                      paddingRight: 4,
                      borderTopLeftRadius: 100,
                      borderTopWidth: 2,
                      borderLeftWidth: 2,
                      borderColor: theme.theme.colors.divider,
                      width: 144,
                      height: 76,
                    }}
                    icon={
                      <SimpleLineIcons
                        style={{
                          position: "relative",
                          right: 50,
                          bottom: 62,
                          width: 24,
                          height: 24,
                          overflow: "visible",
                        }}
                        name="magic-wand"
                        size={24}
                        color="#fff"
                      />
                    }
                  ></Button>
                </View>
              )}
            </>
          )}
          <PlayerContextFreezer isVisible={menuShown}>
            <Menu
              visible={menuShown}
              pinnedSections={pinnedSectionObjects.map((s) => s.section)}
              onPressFavorite={togglePinnedSection}
              onBackgroundPress={onMenuPress}
              onNavigationPress={onNavigationPress}
              onSwipeLeft={swipeLeftHandler}
              onSwipeRight={swipeRightHandler}
            />
          </PlayerContextFreezer>
        </View>
        <ManaBar />
      </View>
    ),
    [
      bottomOpacityAnim,
      theme,
      menuShown,
      mainSections,
      onMenuPress,
      onNavigationPress,
      onSpellMenuPress,
      onEventMenuPress,
      height,
      swipeLeftHandler,
      swipeRightHandler,
      showBottomMenu,
      putCastSpellOnBottomMenu,
      leftHandedMode,
      backCallback,
      JSON.stringify(pinnedSectionObjects),
    ],
  );

  const headerContainerStyle = useMemo(
    () => [
      styles.headerContainer,
      { borderBottomColor: theme.theme.colors.background },
    ],
    [theme],
  );

  const statusBarProps = useMemo(
    () => ({
      translucent: true,
      backgroundColor: "transparent",
      barStyle: "light-content" as StatusBarStyle,
    }),
    [],
  );

  const menuButton = useMemo(
    () => (
      <TouchableOpacity
        accessibilityLabel="Menu"
        accessibilityRole="button"
        onPressIn={onMenuPress}
        hitSlop={{ top: 20, left: 20, bottom: 20, right: 20 }}
      >
        <MaterialIcons name="menu" size={24} color="#fff" />
      </TouchableOpacity>
    ),
    [onMenuPress],
  );

  const accessibilityContext = useContext(AccessibilityContext);

  const onMagicTap = useCallback(() => {
    if (!activeSections.includes("Exploration")) {
      return;
    }
    const isPaused = hasFlag(
      playerContextRef.current,
      "combat_paused_magic_tap",
    );
    playerContextRef.current.apply((state) =>
      isPaused
        ? clearFlag("combat_paused_magic_tap")(state)
        : setFlag("combat_paused_magic_tap")(state),
    );
    accessibilityContext.announceForAccessibility(
      isPaused ? "Resuming Exploration" : "Pausing Exploration",
    );
  }, [accessibilityContext, activeSections.join()]);

  const rightHeaderContainer = useMemo(
    () => (
      <View style={styles.rightHeaderContainer}>
        {!(showBottomMenu && putCastSpellOnBottomMenu) && (
          <TouchableOpacity
            style={styles.rightHeaderIcon}
            onPressIn={onSpellMenuPress}
            accessibilityLabel="Cast Spell"
            accessibilityRole="button"
            hitSlop={{ top: 20, left: 20, bottom: 20, right: 20 }}
          >
            <SimpleLineIcons name="magic-wand" size={24} color="#fff" />
          </TouchableOpacity>
        )}
        <TouchableOpacity
          accessibilityLabel="Events"
          accessibilityRole="button"
          style={styles.rightHeaderIcon}
          onPressIn={onEventMenuPress}
          hitSlop={{ top: 20, left: 10, bottom: 20, right: 20 }}
        >
          <MaterialIcons name="notifications" size={24} color="#fff" />
          {ongoingEventCount > 0 && (
            <Badge
              value={ongoingEventCount}
              status="error"
              containerStyle={styles.notificationsBadge}
            />
          )}
        </TouchableOpacity>
      </View>
    ),
    [
      onEventMenuPress,
      ongoingEventCount,
      showBottomMenu,
      putCastSpellOnBottomMenu,
    ],
  );

  return useMemo(() => {
    if (layout.wideLayout) {
      return (
        <GestureHandlerRootView
          style={[
            styles.maxContainer,
            { backgroundColor: theme.theme.colors.background },
          ]}
          onMagicTap={onMagicTap}
        >
          <GameTickProcessor
            setPlayerState={props.setPlayerState}
            onLoadingProgress={props.onLoadingProgress}
          />
          <Header
            containerStyle={headerContainerStyle}
            statusBarProps={statusBarProps}
            leftComponent={undefined}
            centerComponent={centerComponent}
            rightComponent={
              !layout.showEventsMenu ? (
                <View style={styles.rightHeaderContainer}>
                  {!layout.showSpellMenu && (
                    <TouchableOpacity
                      style={styles.rightHeaderIcon}
                      onPressIn={onSpellMenuPress}
                      accessibilityLabel="Cast Spell"
                      accessibilityRole="button"
                      hitSlop={{ top: 20, left: 20, bottom: 20, right: 20 }}
                    >
                      <SimpleLineIcons
                        name="magic-wand"
                        size={24}
                        color="#fff"
                      />
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity
                    style={styles.rightHeaderIcon}
                    onPressIn={onEventMenuPress}
                    accessibilityLabel="Events"
                    accessibilityRole="button"
                    hitSlop={{ top: 20, left: 10, bottom: 20, right: 20 }}
                  >
                    <MaterialIcons
                      name="notifications"
                      size={24}
                      color="#fff"
                    />
                    {ongoingEventCount > 0 && (
                      <Badge
                        value={ongoingEventCount}
                        status="error"
                        containerStyle={styles.notificationsBadge}
                      />
                    )}
                  </TouchableOpacity>
                </View>
              ) : undefined
            }
          />
          <SafeAreaView
            onMagicTap={onMagicTap}
            style={styles.safeAreaContainer}
          >
            <EventOverlay />
            {contentsWide}
          </SafeAreaView>
        </GestureHandlerRootView>
      );
    }
    return (
      <GestureHandlerRootView
        style={[
          styles.maxContainer,
          { backgroundColor: theme.theme.colors.background },
        ]}
        onMagicTap={onMagicTap}
      >
        <GameTickProcessor
          setPlayerState={props.setPlayerState}
          onLoadingProgress={props.onLoadingProgress}
        />
        <Header
          containerStyle={headerContainerStyle}
          statusBarProps={statusBarProps}
          leftComponent={menuButton}
          centerComponent={centerComponent}
          rightComponent={rightHeaderContainer}
        />
        <SafeAreaView onMagicTap={onMagicTap} style={styles.safeAreaContainer}>
          <EventOverlay />
          {contents}
        </SafeAreaView>
      </GestureHandlerRootView>
    );
  }, [
    props.setPlayerState,
    props.onLoadingProgress,
    onMenuPress,
    onSpellMenuPress,
    onEventMenuPress,
    mainSections,
    ongoingEventCount,
    menuShown,
    rightMenuShown,
    onNavigationPress,
    JSON.stringify(layout),
    height,
    bottomOpacityAnim,
    theme,
    contents,
    contentsWide,
    headerContainerStyle,
    statusBarProps,
    menuButton,
    rightHeaderContainer,
    onMagicTap,
  ]);
}

function OfflineProgress(props: { currentTicks: number; totalTicks: number }) {
  const theme = useTheme();
  return (
    <View
      style={[
        styles.offlineProgressContainer,
        { backgroundColor: theme.theme.colors.background },
      ]}
    >
      <ActivityIndicator size="large" />
      <Text>
        Warping through time...{" "}
        {formatNumber(props.currentTicks, { showDecimals: false })} /{" "}
        {formatNumber(props.totalTicks, { showDecimals: false })}
      </Text>
    </View>
  );
}

export default function App() {
  const [playerState, setPlayerState] = useState(() =>
    clone(PLAYER_INITIAL_STATE),
  );
  const [localUiState, setLocalUiState] = useState<any>({});

  const [processingBigTime, setProcessingBigTime] = useState(false);
  const [totalTicks, setTotalTicks] = useState(0);
  const [currentTicks, setCurrentTicks] = useState(0);

  const onLoadingProgress = useCallback(
    (currentTicks: number, totalTicks: number) => {
      setCurrentTicks(currentTicks);
      setTotalTicks(totalTicks);
      if (currentTicks == totalTicks) {
        setProcessingBigTime(false);
      } else {
        setProcessingBigTime(true);
      }
    },
    [],
  );

  const uiContextValue = useMemo(() => {
    return {
      getUIState: (id: string) => {
        return localUiState?.[id];
      },
      setUIState: (id: string, value: any) => {
        localUiState[id] = value;
        setLocalUiState(localUiState);
      },
    };
  }, [localUiState, setLocalUiState]);

  type TooltipData = {
    content: TooltipContent;
    position: TooltipPosition;
    measurementId: number;
    postMeasurementId: number;
    mouseEnterTimestamp: number;
    mouseLeaveTimestamp: number;
  };
  const [tooltips, setTooltips] = useState<Record<string, TooltipData>>({});

  const tooltipsRef = useRef(tooltips);
  tooltipsRef.current = tooltips;
  const setTooltip = useCallback(
    (
      id: string,
      tooltip: TooltipContent,
      position: TooltipPosition,
      measurementId?: number,
    ) => {
      const newTooltips = Object.assign({}, tooltipsRef.current);
      if (!newTooltips[id]) {
        newTooltips[id] = {
          content: tooltip,
          position: position,
          postMeasurementId: 1,
          measurementId: 0,
          mouseEnterTimestamp: 0,
          mouseLeaveTimestamp: 0,
        };
      }
      newTooltips[id].content = tooltip;
      newTooltips[id].position = position;
      if (measurementId != null) {
        newTooltips[id].postMeasurementId = measurementId;
      }
      // console.log("setting tooltip", id, newTooltips[id].refcount);
      setTooltips(newTooltips);
    },
    [setTooltips],
  );
  const clearTooltip = useCallback(
    (id: string) => {
      const newTooltips = Object.assign({}, tooltipsRef.current);
      const found = newTooltips[id] != null;
      if (!found) {
        // console.log("NOT clearing tooltip", id);
        return;
      }
      newTooltips[id].content = null;
      // console.log("clearing tooltip", id, newTooltips[id].refcount);
      setTooltips(newTooltips);
    },
    [setTooltips],
  );
  const onUnmountTooltip = useCallback(
    (id: string) => {
      const newTooltips = tooltipsRef.current;
      const found = newTooltips[id] != null;
      if (!found) {
        // console.log("NOT unmounting tooltip", id);
        return;
      }
      // console.log("DELETING tooltip", id, newTooltips[id].refcount);
      delete newTooltips[id];
      delete tooltipsRef.current[id];
      setTooltips(Object.assign({}, newTooltips));
    },
    [setTooltips],
  );
  const onMouseLeave = useCallback(
    (id: string) => {
      if (!tooltipsRef.current?.[id]) {
        // console.log("NOT decrementing refcount", id);
        return;
      }
      const newTooltips = Object.assign({}, tooltipsRef.current);
      newTooltips[id].mouseLeaveTimestamp = Date.now();
      // console.log("decrementing refcount", id, newTooltips[id].refcount);
      setTooltips(newTooltips);
    },
    [setTooltips],
  );
  const onMouseEnter = useCallback(
    (id: string, measurementId: number) => {
      const newTooltips = Object.assign({}, tooltipsRef.current);
      if (!newTooltips[id]) {
        newTooltips[id] = {
          content: null,
          position: { containerWidth: 1 },
          postMeasurementId: 1,
          measurementId: 0,
          mouseEnterTimestamp: 0,
          mouseLeaveTimestamp: 0,
        };
      }
      newTooltips[id].measurementId = measurementId;
      newTooltips[id].mouseEnterTimestamp = Date.now();
      // console.log("incrementing refcount", id, newTooltips[id].refcount);
      setTooltips(newTooltips);
    },
    [setTooltips],
  );
  let tooltip: null | TooltipData = null;
  for (let tooltipId in tooltips) {
    if (
      tooltips[tooltipId].mouseEnterTimestamp >
        tooltips[tooltipId].mouseLeaveTimestamp &&
      tooltips[tooltipId].measurementId ===
        tooltips[tooltipId].postMeasurementId &&
      tooltips[tooltipId].content != null
    ) {
      tooltip = tooltips[tooltipId];
      break;
    }
  }
  const tooltipContextValue = useMemo(() => {
    return {
      tooltip: tooltip?.content,
      position: tooltip?.position ?? { containerWidth: 1 },
    };
  }, [tooltip?.content, tooltip?.position]);
  const tooltipControlContextValue = useMemo(() => {
    return {
      setTooltip: setTooltip,
      clearTooltip: clearTooltip,
      onMouseLeave: onMouseLeave,
      onMouseEnter: onMouseEnter,
      onUnmountTooltip: onUnmountTooltip,
    };
  }, [setTooltip, clearTooltip, onMouseLeave, onMouseEnter, onUnmountTooltip]);

  const [loading, setLoading] = useState(true);
  const [criticalError, setCriticalError] = useState(false);
  const [lessCriticalError, setLessCriticalError] = useState(false);

  const playerStateRef = useRef(playerState);
  playerStateRef.current = playerState;

  const apply = useCallback(
    (transform: PlayerContextTransform) => {
      const newState = applyAllTransforms(playerStateRef.current, transform);
      newState.global.appVersionTimestamp = getCurrentVersionTimestamp() || 0;
      newState.global.appVersionString = getCurrentVersion() ?? "0.0.0";
      newState.global.isFullVersionSave = !Demo.isDemo();

      setPlayerState(newState);
    },
    [playerStateRef, setPlayerState],
  );

  const overwrite = useCallback(
    (transform: PlayerContextTransform) => {
      setPlayerState(transform(playerStateRef.current));
      clearAllCaches();
    },
    [playerStateRef, setPlayerState],
  );

  const playerContextValue = useMemo(() => {
    return {
      ...playerState,
      apply: apply,
      overwrite: overwrite,
    };
  }, [playerState, apply, overwrite]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const loadedState = await loadData();
        setPlayerState(loadedState);
        clearAllCaches();
        await nextFrame();
        setLoading(false);
      } catch (error) {
        console.error("Error loading data");
        setCriticalError(true);
      }
    };

    fetchData();
  }, []);

  const [lastTimeFailedToSave, setLastTimeFailedToSave] = useState(0);

  useEffect(() => {
    const storeData = async () => {
      if (criticalError || lessCriticalError) {
        return;
      }
      try {
        await saveData(playerState);
        if (lastTimeFailedToSave > 0) {
          setLastTimeFailedToSave(0);
        }
      } catch (error) {
        console.error("Error saving data", error);
        if (
          lastTimeFailedToSave > 0 &&
          lastTimeFailedToSave <
            Math.floor(getSecondsPlayed(playerState) / 5.0) - 10.0
        ) {
          setLessCriticalError(true);
        } else {
          if (lastTimeFailedToSave <= 0) {
            setLastTimeFailedToSave(
              Math.floor(getSecondsPlayed(playerState) / 5.0),
            );
          }
        }
      }
    };
    if (!loading) {
      storeData();
    }
  }, [
    Math.floor(getSecondsPlayed(playerState) / 5.0),
    loading,
    lastTimeFailedToSave,
    setLastTimeFailedToSave,
    criticalError,
    lessCriticalError,
  ]);

  const [isTransition, setTransition] = useTransition();

  const theme = useMemo(() => {
    return createTheme({
      lightColors:
        primaryColorsByElement?.[
          (playerContextValue.global.overrideElementDisplay != "no"
            ? playerContextValue.global.overrideElementDisplay
            : playerContextValue.primaryElement) || "Default"
        ]?.lightColors,
      darkColors:
        primaryColorsByElement?.[
          (playerContextValue.global.overrideElementDisplay != "no"
            ? playerContextValue.global.overrideElementDisplay
            : playerContextValue.primaryElement) || "Default"
        ]?.darkColors,

      mode: playerContextValue.global.themeMode,
    });
  }, [
    primaryColorsByElement,
    playerContextValue.global.overrideElementDisplay,
    playerContextValue.primaryElement,
    playerContextValue.global.themeMode,
  ]);

  const shouldShowIntro =
    playerContextValue.primaryElement == undefined ||
    playerContextValue.primarySchool == undefined;

  const [showIntro, setShowIntro] = useState(shouldShowIntro);

  useEffect(() => {
    if (showIntro != shouldShowIntro) {
      setTimeout(() => setShowIntro(shouldShowIntro), 0);
    }
  }, [showIntro, shouldShowIntro, setTransition, setShowIntro]);

  const { height, width } = useWindowDimensions();

  const copyToClipboard = useCallback(async () => {
    const serializedData = await serializeSaveData(playerStateRef.current);
    await Clipboard.setStringAsync(serializedData);
  }, []);

  const saveToFileCallback = useCallback(async () => {
    const result = await saveToFile(playerStateRef.current);
  }, []);

  const copyAsyncStorageToClipboard = useCallback(async () => {
    const jsonValue = await AsyncStorage.getItem("PlayerContextState");
    await Clipboard.setStringAsync(jsonValue ?? "null");
  }, []);

  const criticalErrorView = useMemo(
    () => (
      <View style={styles.errorContainer}>
        <View style={styles.errorWrapper}>
          <View style={styles.errorHeader}>
            <Text h2>Something went very wrong!</Text>
          </View>
          <Text>
            It looks like an unexpected error happened while loading the game.
            Please try closing the app and opening it again. If the error
            persists, please contact magic.research.game@gmail.com and send your
            debug saved data for further support.
          </Text>
          <RNEButton
            onPress={copyAsyncStorageToClipboard}
            title="Copy debug save data to clipboard"
            containerStyle={styles.saveButton}
          />
        </View>
      </View>
    ),
    [copyToClipboard, saveToFileCallback],
  );

  const errorView = useMemo(
    () => (
      <View style={styles.errorContainer}>
        <View style={styles.errorWrapper}>
          <View style={styles.errorHeader}>
            <Text h2>Something went wrong!</Text>
          </View>
          <Text>
            It looks like an unexpected error happened. Please try closing the
            app and opening it again. If the error persists, please contact
            magic.research.game@gmail.com and send your saved data for further
            support.
          </Text>
          <RNEButton
            onPress={copyToClipboard}
            title="Copy savefile to clipboard"
            containerStyle={styles.saveButton}
          />
          {SAVE_TO_FILE_SUPPORTED && (
            <RNEButton
              onPress={saveToFileCallback}
              title="Save to file"
              containerStyle={styles.saveButton}
            />
          )}
        </View>
      </View>
    ),
    [copyToClipboard, saveToFileCallback],
  );

  const [accessibilityItems, setAccessibilityItems] = useState<
    { text: string; key: string }[]
  >([]);

  const wakeLock = useMemo(
    () => (playerContextValue.global.wakeLock ? <KeepAwake /> : null),
    [playerContextValue.global.wakeLock],
  );

  const accessibilityItemsRef = useRef(accessibilityItems);
  accessibilityItemsRef.current = accessibilityItems;

  const accessibilityContext = useMemo(() => {
    return Platform.select({
      default: {
        announceForAccessibility: (announcement: string) =>
          AccessibilityInfo.announceForAccessibilityWithOptions(announcement, {
            queue: false,
          }),
      },
      web: {
        announceForAccessibility: (announcement: string) => {
          const newItem = { text: announcement, key: Math.random().toString() };
          const oldItem = accessibilityItemsRef.current?.[0];
          if (oldItem?.text == newItem.text) {
            setAccessibilityItems([]);
            // Fix for same attack used in quick succession...
            setTimeout(() => setAccessibilityItems([newItem]), 100);
          } else {
            setAccessibilityItems([newItem]);
          }
        },
      },
    });
  }, []);

  const [visibilityState, setVisibilityState] = useState("visible");

  useEffect(() => {
    if (Platform.OS != "web") {
      return;
    }

    const onVisibilityChange = () => {
      setVisibilityState(document.visibilityState);
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  useEffect(() => {
    if (
      Platform.OS == "web" &&
      visibilityState == "visible" &&
      JSON.stringify(getLayoutType(height, width)) ==
        JSON.stringify(getLayoutType(height, width + 1))
    ) {
      // Hack fix for alt tab out and back into window breaking screen readers
      // Does not work if window is maximized
      window.resizeBy(1, 0);
      window.resizeBy(-1, 0);
    }
  }, [visibilityState]);

  const offlineProgressView = useMemo(
    () => (
      <View style={[{ flex: 1 }, !processingBigTime && { display: "none" }]}>
        <SafeAreaView style={[styles.safeAreaContainer, styles.centerAligned]}>
          <OfflineProgress
            currentTicks={currentTicks}
            totalTicks={totalTicks}
          />
        </SafeAreaView>
      </View>
    ),
    [currentTicks, totalTicks, processingBigTime],
  );

  const accessibilityView = useMemo(
    () =>
      Platform.OS === "web" &&
      visibilityState == "visible" && (
        <div style={styles.accessibilityView} aria-live="polite" role="status">
          {accessibilityItems.map((item) => (
            <div key={item.key} role="none">
              <Text key={item.key}>{item.text}</Text>
            </div>
          ))}
        </div>
      ),
    [accessibilityItems.map((item) => item.key).join(), visibilityState],
  );

  const mainView = useMemo(
    () => (
      <View
        style={[{ flex: 1 }, processingBigTime && { display: "none" }]}
        nativeID={theme.mode}
      >
        <PlayerContextFreezer isVisible={!showIntro && !processingBigTime}>
          <MainAppContents
            setPlayerState={setPlayerState}
            onLoadingProgress={onLoadingProgress}
          />
        </PlayerContextFreezer>
      </View>
    ),
    [
      processingBigTime,
      theme.mode,
      showIntro,
      setPlayerState,
      onLoadingProgress,
    ],
  );

  const safeAreaProviderStyle = useMemo(
    () => [
      {
        backgroundColor:
          theme.mode === "dark"
            ? theme.darkColors?.background
            : theme.lightColors?.background,
      },
      Platform.OS === "web" && { height: height, flex: "auto" },
    ],
    [theme, height],
  );

  if (criticalError) {
    return criticalErrorView;
  }

  if (lessCriticalError) {
    return errorView;
  }

  if (loading || shouldShowIntro != showIntro) {
    return (
      <ErrorBoundary errorFallback={errorView}>
        <AccessibilityContext.Provider value={accessibilityContext}>
          <Loading theme={theme} />
        </AccessibilityContext.Provider>
      </ErrorBoundary>
    );
  }

  if (showIntro) {
    return (
      <SafeAreaProvider
        initialMetrics={initialWindowMetrics}
        // @ts-expect-error In react-native-web, flex: "auto" works
        style={safeAreaProviderStyle}
      >
        <ThemeProvider theme={theme}>
          <ErrorBoundary errorFallback={errorView}>
            <AccessibilityContext.Provider value={accessibilityContext}>
              <TooltipContext.Provider value={tooltipContextValue}>
                <TooltipControlContext.Provider
                  value={tooltipControlContextValue}
                >
                  <PlayerContext.Provider value={playerContextValue}>
                    <UIContext.Provider value={uiContextValue}>
                      <ZoomChecker />
                      <FullScreenListener />
                      <ThemeUpdater />
                      {wakeLock}
                      <IntroContents />
                      {!playerContextValue.global.hideTooltips && (
                        <TooltipDisplay />
                      )}
                    </UIContext.Provider>
                  </PlayerContext.Provider>
                </TooltipControlContext.Provider>
              </TooltipContext.Provider>
            </AccessibilityContext.Provider>
          </ErrorBoundary>
        </ThemeProvider>
      </SafeAreaProvider>
    );
  }

  return (
    <SafeAreaProvider
      initialMetrics={initialWindowMetrics}
      // @ts-expect-error In react-native-web, flex: "auto" works
      style={safeAreaProviderStyle}
    >
      <ThemeProvider theme={theme}>
        <ErrorBoundary errorFallback={errorView}>
          <AccessibilityContext.Provider value={accessibilityContext}>
            <TooltipContext.Provider value={tooltipContextValue}>
              <TooltipControlContext.Provider
                value={tooltipControlContextValue}
              >
                <PlayerContext.Provider value={playerContextValue}>
                  <UIContext.Provider value={uiContextValue}>
                    <ZoomChecker />
                    <FullScreenListener />
                    <SaveDataBackupper />
                    <OrientationLockManager />
                    <ThemeUpdater />
                    {wakeLock}
                    {accessibilityView}
                    {offlineProgressView}
                    {mainView}
                    {!playerContextValue.global.hideTooltips && (
                      <TooltipDisplay />
                    )}
                  </UIContext.Provider>
                </PlayerContext.Provider>
              </TooltipControlContext.Provider>
            </TooltipContext.Provider>
          </AccessibilityContext.Provider>
        </ErrorBoundary>
      </ThemeProvider>
    </SafeAreaProvider>
  );
}

/*export default function Test() {
  return (
    <Markdown debugPrintTree>
      Focus your thoughts :close: and gather mana. And don't forget to lock the
      door!
    </Markdown>
  );
}*/

const headerPadding =
  Platform.OS === "android" ? 16 : Platform.OS === "web" ? 8 : 0;

const styles = StyleSheet.create({
  headerContainer: {
    paddingTop: headerPadding,
  },
  rightHeaderContainer: {
    flexDirection: "row",
  },
  rightHeaderIcon: {
    marginLeft: 20,
  },
  gestureRecognizer: {
    flex: 1,
  },
  safeAreaContainer: {
    flex: 1,
    justifyContent: "center",
  },
  centerAligned: {
    alignItems: "center",
  },
  maxContainer: {
    flex: 1,
    height: "100%",
  },
  biggerContainer: {
    flex: 1,
    backgroundColor: "#fff",
  },
  mainContainerPhone: {
    overflow: "hidden",
  },
  introContainer: {
    flex: 1,
    alignItems: "center",
  },
  horizontalContainer: {
    flexDirection: "row",
    alignItems: "stretch",
    overflow: "hidden",
    flex: 1,
  },
  mainContainer: {
    flex: 1,
  },
  webMenuContainer: {
    width: 284,
    paddingRight: 8,
  },
  webRightMenuContainer: {
    width: 300,
    paddingLeft: 8,
  },
  notificationsBadge: {
    position: "absolute",
    top: -8,
    right: -8,
  },
  menuContainer: {
    position: "absolute",
  },
  container: {
    padding: 8,
  },
  centerText: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  offlineProgressContainer: {
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  saveButton: {
    marginTop: 8,
  },
  errorContainer: {
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  errorWrapper: {
    maxWidth: 600,
    marginHorizontal: 16,
  },
  errorHeader: {
    marginBottom: 8,
  },
  accessibilityView: {
    height: 0,
    width: 0,
    overflow: "hidden",
    position: "absolute",
    left: 100,
    top: 100,
  },
  accessibilityHidden: {
    width: 1,
    height: 1,
    position: "absolute",
    left: 0,
    top: 0,
  },
  closeButtonInner: {
    paddingHorizontal: 0,
    paddingVertical: 0,
    height: 32,
    width: 32,
    borderRadius: 16,
  },
  pinButtonInner: {
    paddingHorizontal: 0,
    paddingVertical: 0,
    height: 32,
    width: 32,
  },
});
